import { useState } from 'react'
import { Control, FieldValues, Path, RegisterOptions } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InputControl } from './InputControl'

import { inputValidations } from '@/utils/inputValidations'

interface PasswordControlProps<T extends FieldValues> {
    name: Path<T>
    control: Control<T>
    rules?: Omit<
        RegisterOptions<T, Path<T>>,
        'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'
    >
    label?: string
    className?: string
    showStrength?: boolean
}

interface StrengthMeta {
    label: string
    color: string
    width: string
}

interface PasswordRequirements {
    minLength: boolean
    hasSpecialChar: boolean
    hasNumber: boolean
}

interface PasswordStrengthPanelProps {
    requirements: PasswordRequirements
    strengthMeta: StrengthMeta
    title: string
    labels: {
        strength: string
        minLength: string
        specialChar: string
        number: string
    }
}

const getRequirements = (password: string): PasswordRequirements => ({
    minLength: password.length >= 8,
    hasSpecialChar: inputValidations.isSpecialChar.test(password),
    hasNumber: /\d/.test(password)
})

const getStrengthMeta = (
    strength: number,
    labels: {
        secure: string
        medium: string
        weak: string
    }
): StrengthMeta => {
    switch (strength) {
        case 3:
            return {
                label: labels.secure,
                color: 'bg-green-600',
                width: 'w-full'
            }
        case 2:
            return {
                label: labels.medium,
                color: 'bg-yellow-500',
                width: 'w-2/3'
            }
        default:
            return {
                label: labels.weak,
                color: 'bg-red-600',
                width: 'w-1/3'
            }
    }
}

const getPasswordRules = <T extends FieldValues>(
    showStrength: boolean,
    rules: PasswordControlProps<T>['rules'],
    labels: {
        minLength: string
        pattern: string
    }
): PasswordControlProps<T>['rules'] => {
    if (!showStrength) {
        return rules
    }

    return {
        ...rules,
        minLength: {
            value: 8,
            message: labels.minLength
        },
        pattern: {
            value: /^(?=.*[!@#$%^&*#_])(?=.*\d).+$/,
            message: labels.pattern
        }
    }
}

const PasswordStrengthPanel = ({
    requirements,
    strengthMeta,
    title,
    labels
}: PasswordStrengthPanelProps): JSX.Element => (
    <div className="absolute left-0 right-0 z-40 p-2 mt-2 bg-white rounded shadow-md dark:bg-white-800">
        <div className="mb-3 font-bold dark:text-black">{title}</div>
        <div
            className={`h-2 rounded ${strengthMeta.color} ${strengthMeta.width}`}
        />
        <div className="mt-1 text-sm dark:text-black">{strengthMeta.label}</div>
        <ul className="pl-2 mt-2 ml-2 text-sm">
            <li
                className={
                    requirements.minLength ? 'text-green-600' : 'text-blue-600'
                }
            >
                {requirements.minLength ? '✔' : '✖'} {labels.minLength}
            </li>
            <li
                className={
                    requirements.hasSpecialChar
                        ? 'text-green-600'
                        : 'text-blue-600'
                }
            >
                {requirements.hasSpecialChar ? '✔' : '✖'} {labels.specialChar}
            </li>
            <li
                className={
                    requirements.hasNumber ? 'text-green-600' : 'text-blue-600'
                }
            >
                {requirements.hasNumber ? '✔' : '✖'} {labels.number}
            </li>
        </ul>
    </div>
)

export const PasswordControl = <T extends FieldValues>({
    name,
    control,
    rules,
    label,
    className,
    showStrength = true,
    ...props
}: PasswordControlProps<T>): React.ReactElement => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false)
    const [password, setPassword] = useState('')
    const [isFocused, setIsFocused] = useState(false)
    const { t } = useTranslation()

    const togglePasswordVisibility = (): void => {
        setIsPasswordVisible(!isPasswordVisible)
    }
    const requirements = getRequirements(password)
    const strength = Object.values(requirements).filter(Boolean).length
    const isSecure = strength === 3
    const strengthMeta = getStrengthMeta(strength, {
        secure: t('passwordStrength.secure'),
        medium: t('passwordStrength.medium'),
        weak: t('passwordStrength.weak')
    })
    const strengthPanelLabels = {
        strength: t('passwordStrength.title'),
        minLength: t('passwordStrength.atLeast8Characters'),
        specialChar: t('passwordStrength.atLeastOneSpecialCharacter'),
        number: t('passwordStrength.atLeastOneNumber')
    }
    const rulesWithStrength = getPasswordRules(showStrength, rules, {
        minLength: t('passwordStrength.passwordMustBeAtLeast8CharactersLong'),
        pattern: t(
            'passwordStrength.passwordMustContainAtLeastOneSpecialCharacterAndOneNumber'
        )
    })

    return (
        <div className={`relative ${className}`}>
            <div className="relative">
                <InputControl
                    name={name}
                    control={control}
                    label={label}
                    type={isPasswordVisible ? 'text' : 'password'}
                    onChangeCustom={(value) => setPassword(value)}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    rules={rulesWithStrength}
                    {...props}
                    passwordInput={true}
                    allowedPattern={inputValidations.passwordPattern}
                    showPassword={togglePasswordVisibility}
                    isPasswordVisible={isPasswordVisible}
                />
            </div>
            {isFocused && showStrength && !isSecure && (
                <PasswordStrengthPanel
                    requirements={requirements}
                    strengthMeta={strengthMeta}
                    title={strengthPanelLabels.strength}
                    labels={strengthPanelLabels}
                />
            )}
        </div>
    )
}
